/* eslint-disable react/jsx-pascal-case */

import React from 'react'
import { shape, string, array, node } from 'prop-types'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import { themeGet } from '@styled-system/theme-get'
import {
  Box,
  Button,
  Flex,
  Link,
  Text,
  getPaletteColor
} from 'pcln-design-system'
import { WebPBackgroundImage } from '@pcln/content-tile'
import { usePartner } from '@pcln/whitelabel-components'
import analytics from '../../utils/analytics'
import {
  ANALYTICS_CATEGORY_FLIGHTS,
  ANALYTICS_CATEGORY_HOTELS,
  ANALYTICS_CATEGORY_PACKAGES,
  ANALYTICS_CATEGORY_HOME
} from '../../constants/analytics'

const BannerWrapper = styled(Box)`
  border-bottom: 1px solid ${getPaletteColor('border.base')};
`

const FlexDesktopOnly = styled(Flex)`
  display: none;
  ${themeGet('mediaQueries.lg')} {
    display: flex;
    flex: 1;
  }
`

const StyledWebPImage = styled(WebPBackgroundImage)`
  visibility: visible;
  background-position: right;
  background-color: transparent;
  background-size: ${props => props.imageSize};
  flex: 1;
`

const WrapperFlex = styled(Flex)`
  margin: 0 auto;
  max-width: 1280px;
`

const gaCategories = {
  '/flights': ANALYTICS_CATEGORY_FLIGHTS,
  '/hotels': ANALYTICS_CATEGORY_HOTELS,
  '/vacationpackages': ANALYTICS_CATEGORY_PACKAGES,
  '/': ANALYTICS_CATEGORY_HOME
}

const GenericBanner = props => {
  const router = useRouter()
  const pathname = router?.pathname
  const gaCategory = gaCategories[pathname] || ''
  const {
    content,
    contentFontColor,
    headingFontColor,
    smallHeadingFontColor
  } = props
  const {
    buttons,
    contentText,
    gaAction,
    headingText,
    smallHeadingText,
    image,
    imageWebP,
    imageSize
  } = content

  const partner = usePartner()
  const adBannerButtons =
    buttons &&
    buttons.map(button => {
      return (
        <Link
          key={`${button.gaLabel} Button`}
          href={button.linkUrl}
          target="_blank"
          rel="noopener"
          aria-label={`${button.gaLabel} Promo link`}
          onClick={() => {
            if (typeof partner === 'undefined') {
              analytics.fireEvent({
                action: gaAction,
                category: gaCategory,
                label: button.gaLabel
              })
            }
          }}
        >
          <Button data-testid={`${button.gaLabel}-button`}>
            {button.buttonText}
          </Button>
        </Link>
      )
    })

  return (
    <BannerWrapper width={1} px={3} py={4} color="background.light">
      <WrapperFlex flexWrap="wrap">
        <Box
          my={[3, null, null, 5]}
          px={1}
          textAlign={['center', null, null, 'left']}
          width={[1, null, null, 2 / 5]}
        >
          <Box mb={3}>
            <Text
              caps
              color={smallHeadingFontColor}
              fontSize={0}
              fontWeight="bold"
              mb={3}
            >
              {smallHeadingText}
            </Text>
            <Text
              color={headingFontColor}
              fontSize={5}
              fontWeight="bold"
              mb={3}
            >
              {headingText}
            </Text>
            <Text.span fontSize={3} color={contentFontColor} mb={2}>
              {contentText}
            </Text.span>
          </Box>
          {adBannerButtons}
        </Box>
        <FlexDesktopOnly
          flexDirection="column"
          flexWrap="wrap"
          px={1}
          width={[1, null, null, 3 / 5]}
        >
          <StyledWebPImage
            image={image}
            imageWebP={imageWebP}
            imageSize={imageSize}
            height="100%"
          />
        </FlexDesktopOnly>
      </WrapperFlex>
    </BannerWrapper>
  )
}

GenericBanner.defaultProps = {
  contentFontColor: 'text.light',
  headingFontColor: 'text.base',
  smallHeadingFontColor: 'text.light'
}

GenericBanner.propTypes = {
  content: shape({
    gaAction: string,
    smallHeadingText: string,
    headingText: string,
    contentText: string,
    buttons: array,
    image: string,
    imageWebP: string,
    imageSize: string
  }).isRequired,
  children: node,
  contentFontColor: string,
  headingFontColor: string,
  smallHeadingFontColor: string
}

export default GenericBanner
